import PropTypes from 'prop-types';
import { useState, useCallback, useEffect } from 'react';
import { PdfAnnotator } from 'inklayer-react';
import 'inklayer-react/style';

// InkLayer popup z-index / toolbar overrides are defined in src/index.css
// to avoid mutating the global DOM outside of React lifecycle.

// Set to true to enable debug logging
const DEBUG = false;

function InkLayerPDFViewer({
  url,
  data,
  isFullScreen = false,
  onError,
  onSave,
  onClose,
  title,
  user,
  initialAnnotations
}) {
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(data);

  useEffect(() => {
    if (!(url && url.startsWith('blob:') && !pdfData)) {
      return undefined;
    }

    const controller = new AbortController();

    fetch(url, { signal: controller.signal })
      .then((res) => res.arrayBuffer())
      .then((buffer) => setPdfData(buffer))
      .catch((err) => {
        if (err.name === 'AbortError') {
          return;
        }
        if (DEBUG) console.error('Error converting blob URL:', err);
        setError('Failed to load PDF data');
      });

    return () => {
      controller.abort();
    };
  }, [url, pdfData]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const bodyClass = 'inklayer-pdf-viewer-active';
    document.body.classList.add(bodyClass);

    return () => {
      document.body.classList.remove(bodyClass);
    };
  }, []);

  // Log annotation when added
  const handleAnnotationAdded = useCallback((annotation) => {
    if (DEBUG) {
      console.log('📝 Annotation Added:', {
        id: annotation.id,
        type: annotation.kind,
        pageIndex: annotation.target?.pageIndex,
        author: annotation.meta?.authorId,
        createdAt: annotation.meta?.createdAt,
        geometry: annotation.target?.geometry,
        fullAnnotation: annotation,
      });
    }
    // You can also send this to your backend here
    // await sendAnnotationToBackend(annotation);
  }, []);

  // Log annotation when deleted
  const handleAnnotationDeleted = useCallback((annotationId) => {
    if (DEBUG) {
      console.log('🗑️ Annotation Deleted:', {
        id: annotationId,
        timestamp: new Date().toISOString(),
      });
    }
    // You can also notify your backend here
    // await notifyAnnotationDeletedToBackend(annotationId);
  }, []);

  // Log annotation when updated
  const handleAnnotationUpdated = useCallback((annotation) => {
    if (DEBUG) {
      console.log('✏️ Annotation Updated:', {
        id: annotation.id,
        type: annotation.kind,
        pageIndex: annotation.target?.pageIndex,
        updatedAt: annotation.meta?.updatedAt,
      });
    }
  }, []);

  const handleError = useCallback((err) => {
    if (DEBUG) console.error('❌ PDF Error:', err);
    const errorMessage = err?.message || 'Failed to load PDF';
    setError(errorMessage);
    onError?.(errorMessage);
  }, [onError]);

  const handleLoad = useCallback(() => {
    if (DEBUG) console.log('✅ PDF Loaded Successfully');
    setError(null);
    onError?.(null);
  }, [onError]);

  // Called when PdfAnnotator triggers save (via toolbar or our custom Save button)
  const handleSave = useCallback(
    (annotations) => {
      if (DEBUG) {
        console.log('InkLayerPDFViewer: onSave called with', annotations?.length || 0, 'annotations');
      }
      // Forward to caller
      try {
        onSave?.(annotations);
      } catch (err) {
        if (DEBUG) console.error('Error in onSave prop:', err);
      }
    },
    [onSave]
  );

  if (!url && !data && !pdfData) {
    return (
      <div className="text-center text-gray-400 py-10">
        No PDF available
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 py-10">
        {error}
      </div>
    );
  }

  const currentUser = user || {
    id: 'user-default',
    name: 'Default User',
  };

  if (isFullScreen) {
    return (
      <div className="inklayer-pdf-viewer flex flex-col min-h-screen h-screen bg-white w-full">
        <div className="flex-1 overflow-auto bg-gray-50 min-h-screen" style={{ position: 'relative' }}>
          <PdfAnnotator
            url={pdfData ? undefined : url}
            data={pdfData}
            user={currentUser}
            locale="en-US"
            title={title || 'PDF'}
            layoutStyle={{
              width: '100%',
              height: '100vh',
            }}
            defaultShowAnnotationsSidebar={true}
            theme="orange"
            appearance="auto"
            onAnnotationAdded={handleAnnotationAdded}
            onAnnotationDeleted={handleAnnotationDeleted}
            onAnnotationUpdated={handleAnnotationUpdated}
            onLoad={handleLoad}
            onError={handleError}
            onSave={handleSave}
            actions={(ctx) => (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Do you want to export this PDF ?")) {
                      ctx.exportToPdf?.('export');
                    }
                  }}
                  className="px-3 py-1 rounded-md bg-gray-800 text-white text-sm"
                >
                  Export
                </button>
                <button
                  type="button"
                  onClick={() => ctx.save?.()}
                  disabled={!onSave}
                  className={`px-3 py-1 rounded-md text-sm ${onSave ? 'bg-orange-500 text-white' : 'bg-gray-300 text-gray-600 cursor-not-allowed'}`}
                >
                  Save
                </button>
                {onClose && (
                  <button
                    type="button"
                    onClick={() => onClose?.()}
                    className="px-3 py-1 rounded-md bg-gray-100 text-gray-800 text-sm"
                  >
                    Close
                  </button>
                )}
              </div>
            )}
            initialAnnotations={initialAnnotations}
            annotationPermissions={{
              mode: 'unrestricted',
            }}
          />
        </div>
      </div>
    );
  }

  // Preview mode
  return (
    <div className="inklayer-pdf-viewer relative bg-white h-[90vh] overflow-hidden border rounded-lg shadow-sm" style={{ position: 'relative' }}>
      <div className="h-full w-full">
        <PdfAnnotator
          url={pdfData ? undefined : url}
          data={pdfData}
          user={currentUser}
          locale="en-US"
          title={title || 'PDF'}
          layoutStyle={{
            width: '100%',
            height: '100%',
          }}
          defaultShowAnnotationsSidebar={false}
          theme="orange"
          appearance="auto"
          onAnnotationAdded={handleAnnotationAdded}
          onAnnotationDeleted={handleAnnotationDeleted}
          onAnnotationUpdated={handleAnnotationUpdated}
          onLoad={handleLoad}
          onError={handleError}
          onSave={handleSave}
          actions={(ctx) => (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => ctx.exportToPdf?.('export')}
                className="px-3 py-1 rounded-md bg-gray-800 text-white text-sm"
              >
                Export
              </button>
              <button
                type="button"
                onClick={() => ctx.save?.()}
                disabled={!onSave}
                className={`px-3 py-1 rounded-md text-sm ${onSave ? 'bg-orange-500 text-white' : 'bg-gray-300 text-gray-600 cursor-not-allowed'}`}
              >
                Save
              </button>
              {onClose && (
                <button
                  type="button"
                  onClick={() => onClose?.()}
                  className="px-3 py-1 rounded-md bg-gray-100 text-gray-800 text-sm"
                >
                  Close
                </button>
              )}
            </div>
          )}
          initialAnnotations={initialAnnotations}
          annotationPermissions={{
            mode: 'unrestricted',
          }}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-100/90 flex items-end justify-center p-3 pointer-events-none">
        <div className="text-sm text-gray-600">
          Click to view PDF
        </div>
      </div>
    </div>
  );
}

InkLayerPDFViewer.propTypes = {
  url: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.instanceOf(ArrayBuffer), PropTypes.instanceOf(Uint8Array)]),
  isFullScreen: PropTypes.bool,
  onError: PropTypes.func,
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  title: PropTypes.string,
  initialAnnotations: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
};

InkLayerPDFViewer.defaultProps = {
  url: undefined,
  data: undefined,
  isFullScreen: false,
  user: null,
  onSave: null,
  onClose: null,
  title: undefined,
  initialAnnotations: [],
};

export default InkLayerPDFViewer;
